import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { TabMenu } from "primereact/tabmenu";
import { Tooltip } from "primereact/tooltip";
import { odstampaj } from "./stampaUlazneSaSefa";
import CustomDropdown from "../../../custom/customDropdown";
import { AuthContext } from "../../../Header/auth - context";
import { uzmiGresku } from "../../../custom/greske";
import UnosArtiklaKonfig from "../../Magacin/Tab_components/Artical/Unos_artikla_konfig";

/* Datum se šalje kroz toUtcIso — bez korekcije offseta toISOString ume da
   pomeri datum za dan unazad. */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

/* Polja ulazne fakture — samo ono što se stvarno koristi (21.10.2026).

   Izbor je napravljen po popunjenosti u CYCLE-u (7.670 ulaznih faktura u
   2025–26) i po onome što SEF donosi: kurs EUR, kurs na dan prijema, magacin i
   komitent zaglavlja, carinjenje, knjiženje, mesto izvršenja, referenca, SAP PDV,
   ukupan rabat, povezana izlazna i sve kvačice bili su prazni u 100% faktura.
   Knjižno odobrenje/zaduženje više nije polje fakture nego svoj dokument (KO/KZ).
   Podtip (dinarska/devizna) određuje ekran, storno je status, iznosi su zbir
   stavki, a vrstu i finansijski status izvodi procedura. */
const praznaFaktura = {
    id: null,
    fakturaBr: "",              // naš delovodni broj — dodeljuje ga sistem pri čuvanju
    refNo: "",                  // broj fakture DOBAVLJAČA
    id_klijent: null,
    id_grupa_fakture: null,
    statusUlazne: null,
    napomenaStatusa: "",
    datum: null,                // datum fakture
    datumPrometa: null,         // određuje PDV period
    datumPrijema: null,
    rokDana: null,
    rokPlacanjaDatum: null,     // računa se iz datuma i roka
    valuta: null,               // samo devizna
    kurs: null,                 // samo devizna
    napomena: "",
    netoIznos: null,            // zbir stavki (ili SEF dok stavki nema)
    pdvIznos: null,
    brutoIznos: null,
    /* plaćanje — sa SEF-a: račun primaoca i poziv na broj */
    racunDobavljaca: "",
    modelOdobrenja: "",
    pozivNaBrojOdobrenja: "",
    /* slobodna oznaka fakture — najčešće registracija vozila (kao u CYCLE-u) */
    podpozicija: "",
    devizna: false,
};

const praznaStavka = {
    id: null,
    id_vrsta_troska: null,
    id_podvrsta_troska: null,
    id_artikal: null,
    id_magacin: null,
    id_kategorija_troska: null,
    kolicina: null,
    jedinicaMere: null,
    jedinicnaCena: null,
    rabat: null,
    jedinicnaCenaRabat: null,
    id_zaposleni: null,
    datumIzvrsenja: null,
    stopaPDV: 20,
    netoIznos: null,
    pdvIznos: null,
    brutoIznos: null,
    preostaliIznos: null,
    id_vozilo: null,
    km: null,
    povratPDV: true,
    id_povezana_faktura: null,
    id_radni_nalog: null,
    opis: "",
    trosakNaTuri: false,
};

/* Nivo izmene postojeće fakture (invoice.fn_UlaznaNivoIzmene) — ista pravila
   sprovodi procedura; ekran samo zaključava ono što ona ionako ne bi primila.
     PREGLED    odbijena / stornirana — ništa
     RASPODELA  plaćena ili ispravljena KO/KZ — napomena, plaćanje i raspodela
                troška po stavkama
     OGRANICENO stigla sa SEF-a — podaci dobavljača zaključani
     PUNA       sve */
const POLJA_SEF = ["id_klijent", "refNo", "datum", "datumPrometa", "valuta"];
const POLJA_RASPODELA = ["napomena", "racunDobavljaca", "modelOdobrenja", "pozivNaBrojOdobrenja", "podpozicija"];

const UlaznaFakturaForma = ({ devizna = false, sefZaUnos = null, naUnesenu, idFakture = null, naKoKz }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    const [forma, setForma] = useState({ ...praznaFaktura, devizna });
    const [stavke, setStavke] = useState([]);
    const [salje, setSalje] = useState(false);
    const [poslato, setPoslato] = useState(false);

    /* šifarnici */
    const [sifarnici, setSifarnici] = useState({});

    /* modal stavke */
    const [stavkaVidljiva, setStavkaVidljiva] = useState(false);
    const [stavka, setStavka] = useState({ ...praznaStavka });
    const [stavkaPoslata, setStavkaPoslata] = useState(false);
    /* Raspodela troška (vozilo, zaposleni, KM) je zatvorena dok se ne zatraži. */
    const [raspodelaOtvorena, setRaspodelaOtvorena] = useState(false);
    /* Brisanje ide preko izabranog reda, kao na slici: jedno crveno dugme
       iznad tabele umesto kante u svakom redu. */
    const [izabranaStavka, setIzabranaStavka] = useState(null);
    const [filterStavki, setFilterStavki] = useState(null);
    const [filterOstalo, setFilterOstalo] = useState(null);
    /* Aktivna donja kartica. Kucni obrazac: dugmad + stanje, jer PrimeReact
       TabView u ovoj aplikaciji nema stil i iscrtava se kao spisak. */
    const [donjiTab, setDonjiTab] = useState(0);

    /* mali modal za dodavanje u šifarnik (vrsta / podvrsta / kategorija) */
    const [sifarnikModal, setSifarnikModal] = useState(null); // 'VRSTA' | 'PODVRSTA' | 'KATEGORIJA'
    const [sifarnikNaziv, setSifarnikNaziv] = useState("");
    /* Ista podešavanja kao Ulazne fakture → Podešavanje troškova: vrsta nosi
       trošak ture, podvrsta podrazumevanu stopu i povrat PDV-a. id = izmena. */
    const [sifarnikPodesavanja, setSifarnikPodesavanja] = useState({ id: null, id_vrsta_troska_ture: null, stopaPDV: null, povratPDV: false });

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const zaglavlja = () => ({
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
    });

    /* `putanja` se zadaje samo za endpointe van menadzera koraka (npr. citanje
       PDF specifikacije); tada se `step` ne salje jer ga ta ruta ne zna. */
    const posalji = async (step, telo = {}, putanja = null) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + (putanja || "/invoice/ulazne"), {
            method: "POST",
            headers: zaglavlja(),
            body: JSON.stringify(putanja ? telo : { step, ...telo }),
        });
        const tekst = await r.text();
        const podaci = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(podaci.message || "Radnja nije uspela.");
        return podaci;
    };

    const ucitajSifarnike = () => {
        posalji("INIT")
            .then(setSifarnici)
            .catch(async (e) => {
                const g = await uzmiGresku(e, t("common.greska"));
                poruka("error", t("common.greska"), g.message);
            });
    };

    useEffect(() => { ucitajSifarnike(); /* eslint-disable-next-line */ }, []);

    /* ── Postojeća faktura (otvorena iz pregleda) ── */
    const [nivo, setNivo] = useState({ Nivo: "PUNA", Razlog: null });
    const [sefIdVezan, setSefIdVezan] = useState(null);

    const ucitajFakturu = (id) => {
        if (!id) return;
        const uDatum = (v) => (v ? new Date(v) : null);
        posalji("GET_FAKTURA", { id })
            .then((r) => {
                const h = Array.isArray(r) ? r[0] : r;
                if (!h) return;
                setForma({
                    ...praznaFaktura,
                    ...Object.fromEntries(Object.keys(praznaFaktura).map((k) => [k, h[k] ?? praznaFaktura[k]])),
                    datum: uDatum(h.datum),
                    datumPrometa: uDatum(h.datumPrometa),
                    datumPrijema: uDatum(h.datumPrijema),
                    rokPlacanjaDatum: uDatum(h.rokPlacanjaDatum),
                    devizna: !!h.devizna,
                });
                setNivo({ Nivo: h.Nivo || "PUNA", Razlog: h.Razlog || null });
                setSefIdVezan(h.sefId || null);
                /* Sačuvan kurs je odluka operatera — predlog NBS ga ne prepisuje. */
                kursRucno.current = !!h.kurs;
                setPoslato(false);
            })
            .catch((e) => poruka("error", t("common.greska"), e.message));
    };

    useEffect(() => {
        if (idFakture) ucitajFakturu(idFakture);
        else { setForma({ ...praznaFaktura, devizna }); setNivo({ Nivo: "PUNA", Razlog: null }); setSefIdVezan(null); }
        // eslint-disable-next-line
    }, [idFakture]);

    /* Polje je zaključano po nivou izmene. Nova faktura sa SEF-a zaključava ono
       što je dobavljač poslao (dobavljač samo ako je pronađen u šifarniku). */
    const zakljucano = (polje) => {
        if (nivo.Nivo === "PREGLED") return true;
        if (nivo.Nivo === "RASPODELA") return !POLJA_RASPODELA.includes(polje);
        if (POLJA_SEF.includes(polje)) {
            if (nivo.Nivo === "OGRANICENO") return true;
            if (sefZaUnos && !forma.id) return polje === "id_klijent" ? !!forma.id_klijent : !!forma[polje];
        }
        return false;
    };
    const samoPregled = nivo.Nivo === "PREGLED";
    const samoRaspodela = nivo.Nivo === "RASPODELA";

    const izmeni = (polje, vrednost) => setForma((p) => ({ ...p, [polje]: vrednost }));
    const izmeniStavku = (polje, vrednost) => setStavka((p) => ({ ...p, [polje]: vrednost }));

    /* Rok plaćanja se računa iz datuma fakture i roka u danima — dva polja koja
       moraju da se slažu, pa se ne unose oba ručno. */
    useEffect(() => {
        if (!forma.datum || forma.rokDana == null) return;
        const d = new Date(forma.datum);
        d.setDate(d.getDate() + Number(forma.rokDana || 0));
        setForma((p) => ({ ...p, rokPlacanjaDatum: d }));
    }, [forma.datum, forma.rokDana]);

    /* Zbir stavki puni iznose zaglavlja — operater ih ne prepisuje ručno. */
    useEffect(() => {
        if (!stavke.length) return;
        const zbir = (polje) => stavke.reduce((s, x) => s + Number(x[polje] || 0), 0);
        setForma((p) => ({
            ...p,
            netoIznos: zbir("netoIznos"),
            pdvIznos: zbir("pdvIznos"),
            brutoIznos: zbir("brutoIznos"),
        }));
    }, [stavke]);

    /* Status "Odbijen" traži razlog — isto pravilo proverava i procedura, ovo je
       samo da operater ne šalje uzalud. */
    const idOdbijen = useMemo(() => {
        const nadjen = (sifarnici.StatusiDB || []).find(
            (s) => String(s.label).toLowerCase() === "odbijen");
        return nadjen ? nadjen.value : null;
    }, [sifarnici]);

    const jeOdbijen = idOdbijen != null && forma.statusUlazne === idOdbijen;

    /* Grupa "Faktura magacin" — samo tada stavka nudi artikal, magacin i količinu. */
    const jeMagacin = useMemo(() => {
        const g = (sifarnici.GrupeDB || []).find((x) => x.value === forma.id_grupa_fakture);
        return !!g && /magacin/i.test(String(g.label));
    }, [sifarnici, forma.id_grupa_fakture]);

    /* Stavke se unose TEK kad je faktura odobrena.
       Razlog je poreski, ne tehnički: dok direktor ne prihvati dokument, mi na
       njega nemamo pravo na prethodni porez, pa knjiženje troška po stavkama
       ide pre odluke koja to knjiženje uopšte opravdava. Zaglavlje ostaje
       otvoreno — bez njega se status ne bi ni mogao postaviti. */
    const idOdobren = useMemo(() => {
        const nadjen = (sifarnici.StatusiDB || []).find(
            (s) => String(s.label).toLowerCase() === "odobren");
        return nadjen ? nadjen.value : null;
    }, [sifarnici]);

    /* ── Veza sa SEF sandučetom ──────────────────────────────────────────────
       Fakturu koju operater zavodi najčešće je dobavljač već poslao preko SEF-a.
       Traži se po (PIB dobavljača + broj fakture kod dobavljača) — to su tačno
       dva polja koja operater ovde i kuca. Kad se nađe, forma prikazuje SEF
       status i, direktoru, nudi prihvatanje na licu mesta: do sada se to moglo
       samo sa ekrana „Pregled sa SEF-a", pa je isti čovek morao da ide na drugo
       mesto da bi potvrdio ono što upravo zavodi. */
    const [sefDok, setSefDok] = useState(null);
    const [sefTrazi, setSefTrazi] = useState(false);
    const [sefPotvrda, setSefPotvrda] = useState(null);   // { prihvacen }
    const [sefRazlog, setSefRazlog] = useState("");

    /* Zaključano je SAMO ako je faktura vezana za SEF dokument koji još čeka
       odluku. Faktura koja nije stigla preko SEF-a unosi se ručno, bez čekanja.
       Isti uslov proverava i procedura — ovo je da se ne kuca uzalud. */
    const stavkeZakljucane = !!sefDok && sefDok.CekaOdluku === 1;
    /* Nova stavka / brisanje: ne kad čeka SEF odluku, ne na zatvorenoj ni na
       plaćenoj fakturi (iznos je tada već "potrošen" u izvodu ili KO/KZ).
       Izmena postojeće stavke na plaćenoj fakturi sme — ali samo raspodela. */
    const dodavanjeZabranjeno = stavkeZakljucane || samoPregled || samoRaspodela;
    const izmenaZabranjena = stavkeZakljucane || samoPregled;

    /* Zbir stavki mora da se poklopi sa iznosom koji je dobavljač poslao na
       SEF — razlika znači da je stavka zaboravljena ili pogrešno prekucana.
       Prikazuje se tek kad stavke postoje (pre toga iznos JESTE sa SEF-a). */
    const sefIznos = sefDok?.Amount ?? sefZaUnos?.Amount ?? null;
    const razlikaSef = (() => {
        if (sefIznos == null || !stavke.length) return null;
        const r = Number(forma.brutoIznos || 0) - Number(sefIznos);
        return Math.abs(r) >= 0.01 ? r : null;
    })();

    const [sefSalje, setSefSalje] = useState(false);
    /* Prihvati/Odbij sme direktor I eskalirani korisnik. */
    const [smeOdluku, setSmeOdluku] = useState(false);

    const sefPoziv = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/sef/" + putanja, {
            method: "POST",
            headers: zaglavlja(),
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    useEffect(() => {
        sefPoziv("UlazneInit").then((d) => setSmeOdluku(d?.SmeOdluku === 1)).catch(() => {});
        // eslint-disable-next-line
    }, []);

    /* Popunjavanje iz SEF dokumenta izabranog u „Fakture za unos".

       Dobavljač se traži po PIB-u, ne po nazivu: naziv u SEF-u i naziv u našem
       šifarniku retko budu znak u znak isti, a PIB jeste isti po definiciji.
       Ako dobavljača nema u šifarniku, polje ostaje prazno i operater ga bira —
       bolje nego tiho izabrati pogrešnog. */
    useEffect(() => {
        if (!sefZaUnos) return;
        /* Dok šifarnik nije stigao, ne radi se ništa — inače bi se na prvom
           prolazu javilo „dobavljač nije pronađen" za dobavljača koji postoji,
           samo lista još nije učitana. */
        const spisak = sifarnici.DobavljaciDB || [];
        if (!spisak.length) return;

        const ocisti = (x) => String(x || "").replace(/[\s-]/g, "");
        const pibSef = ocisti(sefZaUnos.SupplierVat);
        const mbSef  = ocisti(sefZaUnos.SupplierMB);

        /* Poredi se po POLJU pib (i matičnom broju kao rezervi). Čitanje PIB-a
           iz teksta labele puca čim naziv firme sam sadrži crticu. */
        const nadjen = spisak.find((d) =>
            (pibSef && ocisti(d.pib) === pibSef) ||
            (mbSef && ocisti(d.maticniBroj) === mbSef)
        ) || spisak.find((d) => {
            const delovi = String(d.label || "").split(" - ");
            return pibSef && delovi.length > 1 && ocisti(delovi[delovi.length - 1]) === pibSef;
        });

        const uDatum = (v) => (v ? new Date(v) : null);
        const valutaSef = (sifarnici.ValuteDB || []).find((v) => v.label === sefZaUnos.Currency);

        setForma((p) => ({
            ...p,
            id_klijent:  nadjen ? nadjen.value : (sefZaUnos.id_klijent || p.id_klijent),
            refNo:       sefZaUnos.DocumentNumber || p.refNo,
            datum:       uDatum(sefZaUnos.SentDate) || p.datum,
            /* Datum prometa sa SEF-a (DeliveryDate) — on određuje PDV period. */
            datumPrometa: uDatum(sefZaUnos.DeliveryDate) || p.datumPrometa,
            datumPrijema: uDatum(sefZaUnos.SentDate) || p.datumPrijema,
            rokPlacanjaDatum: uDatum(sefZaUnos.DueDate) || p.rokPlacanjaDatum,
            valuta:      devizna && valutaSef ? valutaSef.value : p.valuta,
            netoIznos:   sefZaUnos.SumWithoutVat ?? p.netoIznos,
            pdvIznos:    sefZaUnos.VatAmount ?? p.pdvIznos,
            brutoIznos:  sefZaUnos.Amount ?? p.brutoIznos,
        }));

        if (!nadjen && sefZaUnos.SupplierVat) {
            poruka("warn", t("common.upozorenje"),
                t("sefUlazne.dobavljacNijeNadjen", { pib: sefZaUnos.SupplierVat }));
        }
        // eslint-disable-next-line
    }, [sefZaUnos, sifarnici.DobavljaciDB, sifarnici.ValuteDB]);

    /* Pun sadržaj SEF dokumenta (UBL): sve što je dobavljač poslao ulazi u formu
       samo — račun, model i poziv na broj, datum izdavanja i prometa, dospeće,
       napomena, iznosi — a stavke dokumenta postaju predlog stavki fakture.
       Pregled sandučeta (gornji efekat) daje samo osnovno; ovo ga dopunjuje. */
    const [sefPredlog, setSefPredlog] = useState(null);   // stavke sa SEF-a
    const kursRucno = useRef(false);

    const primeniUbl = (u, popuniZaglavlje) => {
        if (!u) return;
        const uDatum = (v) => (v ? new Date(v) : null);
        if (popuniZaglavlje) {
            const spisak = sifarnici.DobavljaciDB || [];
            const ocisti = (x) => String(x || "").replace(/[\s-]/g, "");
            const dob = u.dobavljac?.pib
                ? spisak.find((d) => ocisti(d.pib) === ocisti(u.dobavljac.pib)
                    || (u.dobavljac.mb && ocisti(d.maticniBroj) === ocisti(u.dobavljac.mb)))
                : null;
            const valutaSef = (sifarnici.ValuteDB || []).find((v) => v.label === u.valuta);
            const datum = uDatum(u.datumIzdavanja);
            const dospece = uDatum(u.datumDospeca);
            const rokDana = datum && dospece ? Math.max(0, Math.round((dospece - datum) / 86400000)) : null;
            setForma((p) => ({
                ...p,
                id_klijent: dob ? dob.value : p.id_klijent,
                refNo: u.broj || p.refNo,
                datum: datum || p.datum,
                datumPrometa: uDatum(u.datumPrometa) || datum || p.datumPrometa,
                rokDana: rokDana ?? p.rokDana,
                rokPlacanjaDatum: dospece || p.rokPlacanjaDatum,
                valuta: devizna && valutaSef ? valutaSef.value : p.valuta,
                napomena: u.napomena || p.napomena,
                racunDobavljaca: u.placanje?.racun || p.racunDobavljaca,
                modelOdobrenja: u.placanje?.model || p.modelOdobrenja,
                pozivNaBrojOdobrenja: u.placanje?.poziv || p.pozivNaBrojOdobrenja,
                netoIznos: u.iznosi?.neto ?? p.netoIznos,
                pdvIznos: u.iznosi?.pdv ?? p.pdvIznos,
                brutoIznos: u.iznosi?.bruto ?? p.brutoIznos,
            }));
        }
        setSefPredlog((u.stavke || []).map((s) => ({
            ...s,
            id_vrsta_troska: null,
            id_podvrsta_troska: null,
            povratPDV: true,
        })));
    };

    useEffect(() => {
        if (!sefZaUnos?.id || !(sifarnici.DobavljaciDB || []).length) return;
        sefPoziv("UlazniPodaci", { id: sefZaUnos.id })
            .then((u) => primeniUbl(u, true))
            .catch((e) => poruka("warn", t("common.upozorenje"), e.message));
        // eslint-disable-next-line
    }, [sefZaUnos?.id, sifarnici.DobavljaciDB]);

    /* Faktura vezana za SEF a bez stavki: predlog stavki iz SEF dokumenta. */
    useEffect(() => {
        if (!sefIdVezan) return;
        sefPoziv("UlazniPodaci", { id: sefIdVezan }).then((u) => primeniUbl(u, false)).catch(() => {});
        // eslint-disable-next-line
    }, [sefIdVezan]);

    /* Kurs NBS za deviznu fakturu — predlog na datum prometa; operater ga sme
       promeniti, i tada ga više ne diramo. */
    useEffect(() => {
        if (!devizna || !forma.valuta || samoRaspodela || samoPregled) return;
        if (kursRucno.current && forma.kurs) return;
        const dan = forma.datumPrometa || forma.datum;
        posalji("GET_KURS", { valuta: forma.valuta, datum: toUtcIso(dan) })
            .then((r) => {
                const k = Array.isArray(r) ? r[0] : r;
                if (k?.Kurs) setForma((p) => ({ ...p, kurs: Number(k.Kurs) }));
            })
            .catch(() => {});
        // eslint-disable-next-line
    }, [devizna, forma.valuta, forma.datumPrometa, forma.datum]);

    /* Štampa i prilozi rade nad SEF dokumentom — bez njega nemaju izvor. */
    const [stampa, setStampa] = useState(false);
    const [priloziDialog, setPriloziDialog] = useState(null);

    const odstampajSaSefa = async () => {
        if (!sefDok?.id) return;
        setStampa(true);
        try {
            const d = await sefPoziv("UlazniPdf", { id: sefDok.id });
            const otvoreno = await odstampaj({
                pdfBase64: d.pdfBase64,
                nasBroj: forma.fakturaBr || "",
                proverio: sefDok.ProverioIme || sefDok.OdlucioKorisnik || "",
                datumProvere: sefDok.DatumProvere || "",
                prihvaceno: sefDok.DatumOdluke || "",
                dobavljac: sefDok.SupplierName || "",
            });
            if (!otvoreno) poruka("warn", t("common.upozorenje"), t("kartica.popupBlokiran"));
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setStampa(false);
        }
    };

    const otvoriPriloge = () => {
        if (!sefDok?.id) return;
        setPriloziDialog({ spisak: [], ucitava: true });
        sefPoziv("UlazniPrilozi", { id: sefDok.id })
            .then((d) => setPriloziDialog({ spisak: d.prilozi || [], ucitava: false }))
            .catch((e) => { setPriloziDialog(null); poruka("error", t("common.greska"), e.message); });
    };

    const otvoriPrilog = (p, preuzmi) => {
        sefPoziv("UlazniPrilog", { id: sefDok.id, redni: p.redni })
            .then((d) => {
                const bajtovi = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bajtovi], { type: d.mime || "application/octet-stream" }));
                const a = document.createElement("a");
                a.href = url;
                if (preuzmi) a.download = (d.naziv || "prilog").replace(/[\\/:*?"<>|]/g, "-");
                else { a.target = "_blank"; a.rel = "noopener"; }
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            })
            .catch((e) => poruka("error", t("common.greska"), e.message));
    };

    /* Specifikacija iz PDF-a: procitane stavke sa predlozenim nalozima.
       `potvrdjene` drzi bid-ove koje je operater kvacicom prihvatio. */
    const [spec, setSpec] = useState(null);
    const [specUcitava, setSpecUcitava] = useState(false);
    const [potvrdjene, setPotvrdjene] = useState({});
    const specFajlRef = useRef(null);

    const ucitajSpecifikaciju = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;

        setSpecUcitava(true);
        const citac = new FileReader();
        citac.onload = () => {
            /* Samo base64 deo — data: URL nosi i prefiks tipa. */
            const base64 = String(citac.result || "").split(",")[1] || "";
            posalji("SPEC", { pdfBase64: base64 }, "/invoice/ulazne/specifikacija")
                .then((d) => {
                    setSpec(d);
                    /* Unapred su čekirane samo stavke sa jakim nalazom — ostalo
                       operater bira sam. */
                    const unapred = {};
                    (d.stavke || []).forEach((x) => {
                        if (x.id_radni_nalog && x.Jacina === "OBA_VOZILA") unapred[x.bid] = true;
                    });
                    setPotvrdjene(unapred);
                    if (!d.stavke?.length) poruka("warn", t("common.upozorenje"), d.poruka || t("ulazne.specPrazna"));
                })
                .catch((err) => poruka("error", t("common.greska"), err.message))
                .finally(() => setSpecUcitava(false));
        };
        citac.onerror = () => { setSpecUcitava(false); poruka("error", t("common.greska"), t("ulazne.specCitanje")); };
        citac.readAsDataURL(f);
    };

    /* Svaka potvrđena stavka postaje stavka ulazne fakture sa svojim nalogom.
       Ide redom, ne paralelno: procedura po stavci proverava odobrenje, a red
       grešaka je čitljiviji od deset istovremenih. */
    const sacuvajSpecifikaciju = async () => {
        if (!forma.id) { poruka("warn", t("common.upozorenje"), t("ulazne.prvoSacuvajFakturu")); return; }
        const zaUpis = (spec?.stavke || []).filter((x) => potvrdjene[x.bid] && x.id_radni_nalog);
        if (!zaUpis.length) { poruka("warn", t("common.upozorenje"), t("ulazne.specNistaPotvrdjeno")); return; }

        setSpecUcitava(true);
        let upisano = 0;
        for (const x of zaUpis) {
            try {
                await posalji("SAVE_STAVKA", {
                    id_faktura: forma.id,
                    id_radni_nalog: x.id_radni_nalog,
                    netoIznos: x.iznos,
                    brutoIznos: x.iznos,
                    stopaPDV: 0,
                    pdvIznos: 0,
                    opis: x.opis,
                });
                upisano += 1;
            } catch (e) {
                poruka("error", t("common.greska"), e.message);
                break;
            }
        }
        setSpecUcitava(false);
        if (upisano) {
            poruka("success", t("common.sacuvano"), t("ulazne.specUpisano", { broj: upisano }));
            setSpec(null);
            setPotvrdjene({});
            ucitajStavke();
        }
    };

    const pibDobavljaca = useMemo(() => {
        const nadjen = (sifarnici.DobavljaciDB || []).find((d) => d.value === forma.id_klijent);
        // label je "Naziv - PIB", pa se PIB uzima sa kraja
        const delovi = String(nadjen?.label || "").split(" - ");
        return delovi.length > 1 ? delovi[delovi.length - 1].trim() : null;
    }, [sifarnici, forma.id_klijent]);

    const traziNaSefu = () => {
        if (!forma.id && (!pibDobavljaca || !String(forma.refNo || "").trim())) return;
        setSefTrazi(true);
        sefPoziv("UlazniZaFakturu", {
            id_faktura: forma.id || null,
            pib: pibDobavljaca,
            brojDokumenta: String(forma.refNo || "").trim() || null,
        })
            .then(setSefDok)
            .catch(() => setSefDok(null))
            .finally(() => setSefTrazi(false));
    };

    /* Traži se tek kad su oba podatka tu, i to sa zadrškom — inače bi svaki
       otkucani karakter u polju "Broj fakture" značio jedan poziv. */
    useEffect(() => {
        if (!pibDobavljaca || !String(forma.refNo || "").trim()) { setSefDok(null); return; }
        const tajmer = setTimeout(traziNaSefu, 600);
        return () => clearTimeout(tajmer);
        // eslint-disable-next-line
    }, [pibDobavljaca, forma.refNo, forma.id]);

    const potvrdiSefOdluku = () => {
        if (!sefDok || !sefPotvrda) return;
        if (!sefPotvrda.prihvacen && !sefRazlog.trim()) {
            poruka("warn", t("common.upozorenje"), t("sefUlazne.razlogObavezan"));
            return;
        }
        setSefSalje(true);
        sefPoziv("UlazniOdluka", {
            id: sefDok.id,
            prihvacen: sefPotvrda.prihvacen,
            napomena: sefRazlog.trim() || null,
        })
            .then(async (d) => {
                poruka("success", t("common.sacuvano"), d.message);
                /* Odobren dokument se odmah vezuje za fakturu — dok je id_faktura
                   prazan, dokument stoji u radnoj listi „odobreno, neknjiženo". */
                if (sefPotvrda.prihvacen && forma.id) {
                    try { await sefPoziv("UlazniVeziFakturu", { id: sefDok.id, id_faktura: forma.id }); }
                    catch (_) { /* veza nije kriticna za odluku */ }
                }
                /* Prihvatanje na SEF-u povlači i naš status — to je ista odluka. */
                if (sefPotvrda.prihvacen && idOdobren != null) izmeni("statusUlazne", idOdobren);
                setSefPotvrda(null); setSefRazlog("");
                traziNaSefu();
            })
            .catch((e) => poruka("error", t("common.greska"), e.message))
            .finally(() => setSefSalje(false));
    };

    const nevalja = (v) => poslato && (v === null || v === undefined || v === "");

    /* ---- šifarnik: dodavanje u hodu ---- */
    /* "+" otvara prazan dijalog (nova), olovka izabranu vrstu/podvrstu (izmena). */
    const otvoriSifarnik = (vrsta, idIzmene = null) => {
        const lista = vrsta === "VRSTA" ? sifarnici.VrsteTroskaDB : sifarnici.PodvrsteTroskaDB;
        const r = idIzmene ? (lista || []).find((x) => x.value === idIzmene) : null;
        setSifarnikNaziv(r ? r.label : "");
        setSifarnikPodesavanja({
            id: r ? r.value : null,
            id_vrsta_troska: r?.id_vrsta_troska ?? stavka.id_vrsta_troska,
            id_vrsta_troska_ture: r?.id_vrsta_troska_ture ?? null,
            stopaPDV: r?.stopaPDV != null ? Number(r.stopaPDV) : null,
            povratPDV: r ? r.povratPDV === 1 : false,
        });
        setSifarnikModal(vrsta);
    };

    const sacuvajSifarnik = async () => {
        const korak = sifarnikModal === "VRSTA" ? "SAVE_VRSTA"
            : sifarnikModal === "PODVRSTA" ? "SAVE_PODVRSTA" : "SAVE_KATEGORIJA";
        const sp = sifarnikPodesavanja;
        try {
            const telo = { id: sp.id || 0, naziv: sifarnikNaziv };
            if (korak === "SAVE_VRSTA") telo.id_vrsta_troska_ture = sp.id_vrsta_troska_ture;
            if (korak === "SAVE_PODVRSTA") {
                telo.id_vrsta_troska = sp.id_vrsta_troska;
                telo.stopaPDV = sp.stopaPDV;
                telo.povratPDV = sp.povratPDV ? 1 : 0;   // BIT kao 1/0
            }
            const r = await posalji(korak, telo);
            poruka(String(r.message || "").includes("Nema izmena") ? "warn" : "success", t("fakture.ulazne"), r.message);
            setSifarnikModal(null);
            ucitajSifarnike();
            /* Nova/izmenjena se odmah bira, da operater ne traži isto po spisku;
               podvrsta odmah daje i svoj PDV stavci. */
            if (korak === "SAVE_VRSTA") izmeniStavku("id_vrsta_troska", r.ID);
            if (korak === "SAVE_PODVRSTA") {
                setStavka((p) => {
                    const s = { ...p, id_podvrsta_troska: r.ID };
                    if (samoRaspodela) return s;
                    s.povratPDV = !!sp.povratPDV;
                    return sp.stopaPDV != null ? preracunaj({ ...s, stopaPDV: Number(sp.stopaPDV) }, true) : s;
                });
            }
            if (korak === "SAVE_KATEGORIJA") izmeniStavku("id_kategorija_troska", r.ID);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        }
    };

    /* ---- stavke ---- */
    /* Stavke se čitaju sa servera — dok faktura nema ID, nema ni stavki. */
    /* Procedura vraća kolone kakve su u tabeli (NetoIznos, StopaPDV…), a forma
       radi sa nazivima iz praznaStavka (netoIznos, stopaPDV…). Bez prevoda su
       tabela i modal ostajali bez iznosa, a zbir zaglavlja pao na 0. */
    const uStavku = (r) => ({
        ...r,
        kolicina: r.Kolicina ?? r.kolicina ?? null,
        jedinicaMere: r.JedinicaMere ?? r.jedinicaMere ?? null,
        jedinicnaCena: r.JedinicnaCena ?? r.jedinicnaCena ?? null,
        rabat: r.Rabat ?? r.rabat ?? null,
        jedinicnaCenaRabat: r.JedinicnaCenaRabat ?? r.jedinicnaCenaRabat ?? null,
        datumIzvrsenja: (r.DatumIzvrsenja ?? r.datumIzvrsenja) ? new Date(r.DatumIzvrsenja ?? r.datumIzvrsenja) : null,
        stopaPDV: r.StopaPDV ?? r.stopaPDV ?? null,
        netoIznos: r.NetoIznos ?? r.netoIznos ?? null,
        pdvIznos: r.PdvIznos ?? r.pdvIznos ?? null,
        brutoIznos: r.BrutoIznos ?? r.brutoIznos ?? null,
        preostaliIznos: r.PreostaliIznos ?? r.preostaliIznos ?? null,
        km: r.KM ?? r.km ?? null,
        povratPDV: (r.PovratPDV ?? r.povratPDV) == null ? true : !!(r.PovratPDV ?? r.povratPDV),
        opis: r.Opis ?? r.opis ?? "",
        trosakNaTuri: !!(r.TrosakNaTuri ?? r.trosakNaTuri),
    });

    const ucitajStavke = (idFakture) => {
        const idf = idFakture || forma.id;
        if (!idf) { setStavke([]); return Promise.resolve(); }
        return posalji("GET_STAVKE", { id_faktura: idf })
            .then((redovi) => setStavke((Array.isArray(redovi) ? redovi : []).map(uStavku)))
            .catch(() => {});
    };

    useEffect(() => { ucitajStavke(); /* eslint-disable-next-line */ }, [forma.id]);

    /* Plaćanja (sravnjenja sa izvodom) i knjižna odobrenja/zaduženja vezana za
       fakturu — samo prikaz; unose se na izvodu odnosno u KO/KZ. */
    const [placanja, setPlacanja] = useState([]);
    const [kokz, setKokz] = useState([]);
    useEffect(() => {
        if (!forma.id) { setPlacanja([]); setKokz([]); return; }
        posalji("GET_PLACANJA", { id_faktura: forma.id }).then((r) => setPlacanja(Array.isArray(r) ? r : [])).catch(() => {});
        posalji("GET_KOKZ", { id_faktura: forma.id }).then((r) => setKokz(Array.isArray(r) ? r : [])).catch(() => {});
        // eslint-disable-next-line
    }, [forma.id]);

    const otvoriStavku = (red) => {
        const s = red
            ? { ...praznaStavka, ...red, trosakNaTuri: !!(red.trosakNaTuri ?? red.TrosakNaTuri) }
            : { ...praznaStavka };
        setStavka(s);
        /* Stavka koja već nosi raspodelu otvara se sa otvorenim delom — inače bi
           izgledalo da podataka nema. */
        setRaspodelaOtvorena(!!(s.id_vozilo || s.id_zaposleni || s.km || s.datumIzvrsenja || s.trosakNaTuri || s.id_radni_nalog));
        setStavkaPoslata(false);
        setStavkaVidljiva(true);
    };

    /* Magacin: neto = količina × cena (sa rabatom). Na ostalim grupama neto se
       unosi direktno, kao u CYCLE-u. */
    useEffect(() => {
        if (!jeMagacin) return;
        const kol = Number(stavka.kolicina || 0);
        const cena = Number(stavka.jedinicnaCena || 0);
        if (!kol || !cena) return;
        const cenaRabat = cena * (1 - Number(stavka.rabat || 0) / 100);
        setStavka((p) => preracunaj({
            ...p,
            jedinicnaCenaRabat: Number(cenaRabat.toFixed(4)),
            netoIznos: Number((kol * cenaRabat).toFixed(2)),
        }, true));
        // eslint-disable-next-line
    }, [jeMagacin, stavka.kolicina, stavka.jedinicnaCena, stavka.rabat]);

    /* PDV se predlaže iz neta i stope, bruto je uvek neto + PDV. Računa se SAMO
       na izmenu korisnika (ne pri otvaranju), da se ne prepiše PDV koji na
       papiru stoji drugačije zaokružen — operater ga sme ispraviti na dinar. */
    const preracunaj = (s, iPdv) => {
        const neto = Number(s.netoIznos || 0);
        const pdv = iPdv ? Number((neto * Number(s.stopaPDV || 0) / 100).toFixed(2)) : Number(s.pdvIznos || 0);
        return { ...s, pdvIznos: pdv, brutoIznos: Number((neto + pdv).toFixed(2)) };
    };
    /* Podvrsta nosi podrazumevani PDV (osiguranje 0% bez povrata, gorivo u
       zemlji 20% sa povratom…) — izbor ga predlaže, operater sme da promeni.
       Na plaćenoj fakturi iznosi su zaključani, pa se PDV tada ne dira. */
    const podvrstaPDV = (idPodvrste) =>
        (sifarnici.PodvrsteTroskaDB || []).find((p) => p.value === idPodvrste) || {};
    const izaberiPodvrstu = (idPodvrste) => {
        const d = podvrstaPDV(idPodvrste);
        setStavka((p) => {
            const s = { ...p, id_podvrsta_troska: idPodvrste };
            if (samoRaspodela) return s;
            if (d.povratPDV != null) s.povratPDV = d.povratPDV === 1;
            if (d.stopaPDV == null) return s;
            return preracunaj({ ...s, stopaPDV: Number(d.stopaPDV) }, true);
        });
    };

    /* Artikal predlaže jedinicu mere — kao u Novoj prijemnici. */
    const [artikalModal, setArtikalModal] = useState(false);
    const izaberiArtikal = (idArtikla) => {
        const a = (sifarnici.ArtikliDB || []).find((x) => x.value === idArtikla);
        setStavka((p) => ({ ...p, id_artikal: idArtikla ?? null, jedinicaMere: a?.JedinicaMere ?? p.jedinicaMere }));
    };

    /* Radni nalozi u periodu oko datuma fakture (±60 dana). Za stavku sa
       vozilom spisak se sužava na ture tog vozila — inače je stotine naloga. */
    const [radniNalozi, setRadniNalozi] = useState([]);
    useEffect(() => {
        if (!forma.id) { setRadniNalozi([]); return; }
        posalji("GET_RADNI_NALOZI", { datum: toUtcIso(forma.datum) })
            .then((r) => setRadniNalozi(Array.isArray(r) ? r : []))
            .catch(() => setRadniNalozi([]));
        // eslint-disable-next-line
    }, [forma.id, forma.datum]);
    const naloziZaVozilo = (idVozila) => (idVozila
        ? radniNalozi.filter((n) => n.id_vozilo === idVozila || n.id_prikljucno === idVozila)
        : radniNalozi);
    const naloziZaStavku = useMemo(() => {
        const lista = naloziZaVozilo(stavka.id_vozilo);
        /* izabran nalog ostaje u spisku i kad ne odgovara vozilu */
        return stavka.id_radni_nalog && !lista.some((n) => n.value === stavka.id_radni_nalog)
            ? [...lista, ...radniNalozi.filter((n) => n.value === stavka.id_radni_nalog)]
            : lista;
        // eslint-disable-next-line
    }, [radniNalozi, stavka.id_vozilo, stavka.id_radni_nalog]);

    /* Tab „Povezani radni nalozi": postojeća stavka ↔ nalog. */
    const [veze, setVeze] = useState([]);
    const [veza, setVeza] = useState({ id_stavka: null, id_radni_nalog: null });
    const [vezaDialog, setVezaDialog] = useState(false);
    const [izabranaVeza, setIzabranaVeza] = useState(null);
    const [filterNaloga, setFilterNaloga] = useState(null);
    const ucitajVeze = () => {
        if (!forma.id) { setVeze([]); return; }
        posalji("GET_NALOZI_FAKTURE", { id_faktura: forma.id })
            .then((r) => setVeze(Array.isArray(r) ? r : []))
            .catch(() => setVeze([]));
    };
    useEffect(() => { ucitajVeze(); /* eslint-disable-next-line */ }, [forma.id, stavke]);
    const stavkeZaVezu = useMemo(() => stavke.map((s) => ({
        value: s.id,
        label: `${s.rb}. ${s.PodvrstaTroskaNaziv || ""} · ${Number(s.brutoIznos ?? s.BrutoIznos ?? 0)
            .toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        id_vozilo: s.id_vozilo,
    })), [stavke]);
    const voziloIzabraneStavke = stavkeZaVezu.find((s) => s.value === veza.id_stavka)?.id_vozilo || null;
    const veziNalog = (idStavke, idNaloga) => {
        posalji("VEZI_STAVKU_NALOG", { id: idStavke, id_radni_nalog: idNaloga })
            .then((r) => {
                poruka("success", t("common.sacuvano"), r.message);
                setVeza({ id_stavka: null, id_radni_nalog: null });
                setVezaDialog(false);
                setIzabranaVeza(null);
                ucitajStavke();
            })
            .catch((e) => poruka("error", t("common.greska"), e.message));
    };

    const izmeniIznos = (polje, vrednost) =>
        setStavka((p) => preracunaj({ ...p, [polje]: vrednost }, polje !== "pdvIznos"));

    const sacuvajStavku = () => {
        setStavkaPoslata(true);
        if (!stavka.id_vrsta_troska || !stavka.id_podvrsta_troska || !stavka.netoIznos
            || stavka.stopaPDV === null || stavka.stopaPDV === undefined) {
            poruka("warn", t("common.upozorenje"), t("ulazne.stavkaObavezna"));
            return;
        }
        if (!forma.id) {
            poruka("warn", t("common.upozorenje"), t("ulazne.prvoSacuvajFakturu"));
            return;
        }
        posalji("SAVE_STAVKA", {
            ...stavka,
            id_faktura: forma.id,
            datumIzvrsenja: toUtcIso(stavka.datumIzvrsenja),
            /* BIT kao 1/0 — procedura tako čita */
            povratPDV: stavka.povratPDV ? 1 : 0,
            trosakNaTuri: stavka.trosakNaTuri ? 1 : 0,
        })
            .then((r) => {
                poruka("success", t("common.sacuvano"), r.message);
                setStavkaVidljiva(false);
                ucitajStavke();
            })
            .catch((e) => poruka("error", t("common.greska"), e.message));
    };

    /* Stavke sa SEF-a → stavke fakture. Dobavljač ne zna našu vrstu/podvrstu
       troška, pa je operater bira (jednom za sve ili po redu); iznosi, stopa i
       opis ostaju tačno onakvi kakve je dobavljač poslao. */
    const [predlogSve, setPredlogSve] = useState({ id_vrsta_troska: null, id_podvrsta_troska: null });
    const [predlogSalje, setPredlogSalje] = useState(false);
    /* Izbor podvrste predlaže i povrat PDV-a sa podvrste. Stopa ostaje ona sa
       SEF-a — nju je poslao dobavljač. */
    const sPodvrstom = (x, polje, v) => {
        const n = { ...x, [polje]: v };
        if (polje === "id_vrsta_troska") n.id_podvrsta_troska = null;
        if (polje === "id_podvrsta_troska") {
            const d = (sifarnici.PodvrsteTroskaDB || []).find((p) => p.value === v);
            if (d?.povratPDV != null) n.povratPDV = d.povratPDV === 1;
        }
        return n;
    };
    const izmeniPredlog = (rb, polje, v) => setSefPredlog((p) => p.map((x) => (x.rb === rb ? sPodvrstom(x, polje, v) : x)));
    const primeniNaSve = (polje, v) => {
        setPredlogSve((p) => ({ ...p, [polje]: v, ...(polje === "id_vrsta_troska" ? { id_podvrsta_troska: null } : {}) }));
        setSefPredlog((p) => p.map((x) => sPodvrstom(x, polje, v)));
    };

    /* Dobavljač koji uvek šalje istu vrstu troška (pumpa → gorivo): kad je
       jedna podvrsta bila na bar 60% njegovih stavki u poslednjih godinu dana,
       predlaže se za sve stavke sa SEF-a. Operater je i dalje menja. */
    useEffect(() => {
        if (!sefPredlog?.length || !forma.id_klijent) return;
        if (sefPredlog.some((x) => x.id_vrsta_troska)) return;
        posalji("GET_PREDLOG_DOBAVLJAC", { id_klijent: forma.id_klijent })
            .then((r) => {
                const d = Array.isArray(r) ? r[0] : null;
                if (!d || Number(d.Udeo) < 0.6) return;
                setPredlogSve({ id_vrsta_troska: d.id_vrsta_troska, id_podvrsta_troska: d.id_podvrsta_troska });
                setSefPredlog((p) => p.map((x) => ({
                    ...x,
                    id_vrsta_troska: d.id_vrsta_troska,
                    id_podvrsta_troska: d.id_podvrsta_troska,
                    povratPDV: d.povratPDV != null ? d.povratPDV === 1 : x.povratPDV,
                })));
            })
            .catch(() => {});
        // eslint-disable-next-line
    }, [sefPredlog?.length, forma.id_klijent]);
    const podvrsteZa = (idVrste) => (sifarnici.PodvrsteTroskaDB || []).filter((p) => !idVrste || p.id_vrsta_troska === idVrste);

    const upisiSefStavke = () => {
        if (!forma.id) { poruka("warn", t("common.upozorenje"), t("ulazne.prvoSacuvajFakturu")); return; }
        if ((sefPredlog || []).some((x) => !x.id_vrsta_troska || !x.id_podvrsta_troska)) {
            poruka("warn", t("common.upozorenje"), t("ulazne.sefStavkeVrsta"));
            return;
        }
        setPredlogSalje(true);
        posalji("SAVE_SEF_STAVKE", {
            id_faktura: forma.id,
            stavke: sefPredlog.map((x) => ({
                id_vrsta_troska: x.id_vrsta_troska,
                id_podvrsta_troska: x.id_podvrsta_troska,
                netoIznos: x.neto,
                stopaPDV: x.stopaPDV ?? 0,
                povratPDV: x.povratPDV ? 1 : 0,
                opis: [x.opis, x.kolicina != null ? `${x.kolicina} ${x.jedinicaMere || ""} × ${x.cena ?? ""}` : null]
                    .filter(Boolean).join(" — "),
            })),
        })
            .then((r) => { poruka("success", t("common.sacuvano"), r.message); setSefPredlog(null); ucitajStavke(); })
            .catch((e) => poruka("error", t("common.greska"), e.message))
            .finally(() => setPredlogSalje(false));
    };

    const obrisiStavku = (red) => {
        if (!red?.id) { setIzabranaStavka(null); return; }
        posalji("DELETE_STAVKA", { id: red.id })
            .then((r) => {
                poruka("success", t("common.sacuvano"), r.message);
                setIzabranaStavka(null);
                ucitajStavke();
            })
            .catch((e) => poruka("error", t("common.greska"), e.message));
    };

    /* ---- snimanje ---- */
    const sacuvaj = async () => {
        setPoslato(true);
        if (!forma.id_klijent || !forma.refNo || !forma.datum || !forma.datumPrometa || !forma.id_grupa_fakture
            || !forma.statusUlazne || (devizna && (!forma.valuta || !forma.kurs))) {
            poruka("warn", t("common.upozorenje"), t("ulazne.obaveznaPolja"));
            return;
        }
        if (jeOdbijen && !String(forma.napomenaStatusa || "").trim()) {
            poruka("warn", t("common.upozorenje"), t("ulazne.razlogObavezan"));
            return;
        }
        setSalje(true);
        try {
            const r = await posalji("SAVE", {
                ...forma,
                datum: toUtcIso(forma.datum),
                datumPrometa: toUtcIso(forma.datumPrometa),
                datumPrijema: toUtcIso(forma.datumPrijema),
                rokPlacanjaDatum: toUtcIso(forma.rokPlacanjaDatum),
                /* BIT ide kao 1/0 — procedura tako čita. Dinarsku valutu i kurs
                   (RSD / 1) postavlja procedura. */
                devizna: devizna ? 1 : 0,
            });
            setForma((p) => ({
                ...p,
                id: r.ID,
                fakturaBr: r.fakturaBr || p.fakturaBr,
            }));
            setPoslato(false);
            poruka("success", t("fakture.ulazne"), r.message);

            /* Veza SEF dokument → naša faktura. Tek time dokument izlazi iz
               spiska „za unos". Ako vezivanje padne, faktura je i dalje snimljena
               a dokument ostaje u spisku — ponovni pokušaj je bezopasan. */
            if (sefZaUnos?.id && r.ID) {
                try {
                    await sefPoziv("UlazniVeziFakturu", { id: sefZaUnos.id, id_faktura: r.ID });
                    naUnesenu?.();
                } catch (e) {
                    poruka("warn", t("common.upozorenje"), t("sefUlazne.vezivanjeNijeUspelo"));
                }
            }
            /* Iz baze: nivo izmene se posle veze sa SEF-om menja (OGRANICENO). */
            if (r.ID) ucitajFakturu(r.ID);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setSalje(false);
        }
    };

    /* Podvrste se sužavaju na izabranu vrstu — jedna vrsta nosi više podvrsta. */
    const podvrsteZaVrstu = useMemo(
        () => (sifarnici.PodvrsteTroskaDB || []).filter(
            (p) => !stavka.id_vrsta_troska || p.id_vrsta_troska === stavka.id_vrsta_troska),
        [sifarnici, stavka.id_vrsta_troska]);

    const nazivIz = (spisak, id) =>
        (sifarnici[spisak] || []).find((x) => x.value === id)?.label || "";

    /* Dugme "+" uz dropdown — isti obrazac kao kod artikala u Novoj prijemnici. */
    const saDodavanjem = (sadrzaj, naKlik, naIzmenu = null) => (
        <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
            <div style={{ flex: 1 }}>{sadrzaj}</div>
            {naIzmenu && (
                <Button type="button" icon="pi pi-pencil" severity="secondary" outlined onClick={naIzmenu}
                    tooltip={t("ulazne.izmeniUSifarniku")} tooltipOptions={{ position: "top" }}
                    style={{ width: "2rem", height: "2rem", flex: "none" }} />
            )}
            <Button type="button" icon="pi pi-plus" severity="info" onClick={naKlik}
                tooltip={t("ulazne.dodajUSifarnik")} tooltipOptions={{ position: "top" }}
                style={{ width: "2rem", height: "2rem", flex: "none" }} />
        </div>
    );

    /* Ostali tabovi donjeg dela (Plaćanja/avans, JCI, Povezane izlazne) još
       nemaju radnje, ali moraju imati istu traku kao Stavke — inače tabela
       skoči uz tabove i deluje kao drugi ekran. */
    const zaglavljeProsto = (postaviFilter) => (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje" />
            <input type="search" placeholder={t('nova.phPretraga')}
                className="p-inputtext p-component"
                onInput={(e) => postaviFilter(e.target.value)} />
        </div>
    );

    /* Zaglavlje tabele stavki je isto kao na Izlaznim fakturama: radnje levo,
       pretraga desno, u samom zaglavlju tabele a ne u traci iznad nje. */
    const zaglavljeStavki = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-plus" severity="info" rounded
                    disabled={dodavanjeZabranjeno}
                    onClick={() => otvoriStavku(null)}
                    data-pr-tooltip={t('ulazne.novaStavka')} />
                <Button type="button" icon="pi pi-minus" severity="danger" rounded
                    disabled={dodavanjeZabranjeno || !izabranaStavka}
                    onClick={() => izabranaStavka && obrisiStavku(izabranaStavka)}
                    data-pr-tooltip={t('ulazne.obrisiStavku')} />
            </div>
            <input type="search" placeholder={t('nova.phPretraga')}
                className="p-inputtext p-component"
                onInput={(e) => setFilterStavki(e.target.value)} />
        </div>
    );

    return (
        /* Klasa novaDinarskaIzlazneForm je namerno ista kao na izlaznim:
           odatle dolaze razmak ispod labele i sirina polja, pa dve forme
           izgledaju isto i ostaju iste kad se stil menja na jednom mestu. */
        <div className="col-12 novaDinarskaIzlazneForm ulaznaFakturaForma">
            <Toast ref={toast} />

            {/* ─────────── SEF traka ───────────
                Pojavljuje se tek kad se za uneti par (dobavljač + broj fakture)
                nađe dokument u SEF sandučetu. Dok je nema, faktura se zavodi
                ručno i SEF nema šta da kaže. */}
            {sefDok && (
                <div className={`ulazne-sef-traka${sefDok.CekaOdluku ? " ceka" : ""}`}>
                    <span className="ulazne-sef-nalaz">
                        <i className="pi pi-cloud" /> {t("sefUlazne.nadjenoNaSefu")}
                    </span>

                    {/* Štampa i prilozi — isti alat kao na izlaznoj fakturi, samo
                        što se ovde štampa dobavljačev dokument sa naše SEF strane. */}
                    <Button type="button" icon="pi pi-print" label={t("nova.stampa")}
                        className="p-button-sm p-button-outlined" loading={stampa}
                        onClick={odstampajSaSefa} />
                    <Button type="button" icon="pi pi-paperclip" label={t("sefUlazne.prilozi")}
                        className="p-button-sm p-button-outlined"
                        onClick={otvoriPriloge} />
                    <span className={`sef-pilula ${sefDok.SefStatus === "ReNotified" ? "sef-st-kritican" : "sef-st-info"}`}>
                        {t("sefUlazne.sefStatus")}: {sefDok.SefStatus || "—"}
                    </span>
                    {sefDok.StatusUlazneNaziv && (
                        <span className={`sef-pilula ${sefDok.CekaOdluku ? "sef-st-upozorenje" : "sef-st-ok"}`}>
                            {sefDok.StatusUlazneNaziv}
                        </span>
                    )}
                    <span className="ulazne-sef-iznos">
                        {Number(sefDok.Amount || 0).toLocaleString("sr-RS", {
                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                        })} {sefDok.Currency || ""}
                    </span>

                    <span style={{ flex: 1 }} />

                    {sefDok.CekaOdluku && smeOdluku && (
                        <>
                            <Button type="button" label={t("sefUlazne.prihvati")} icon="pi pi-check"
                                severity="success" className="p-button-sm"
                                onClick={() => { setSefRazlog(""); setSefPotvrda({ prihvacen: true }); }} />
                            <Button type="button" label={t("sefUlazne.odbij")} icon="pi pi-times"
                                severity="danger" outlined className="p-button-sm"
                                onClick={() => { setSefRazlog(""); setSefPotvrda({ prihvacen: false }); }} />
                        </>
                    )}
                    {sefDok.CekaOdluku && !smeOdluku && (
                        <span className="ulazne-sef-napomena">
                            <i className="pi pi-lock" /> {t("sefUlazne.samoOdlucilac")}
                        </span>
                    )}
                </div>
            )}
            {sefTrazi && (
                <div className="ulazne-sef-traka">
                    <i className="pi pi-spin pi-spinner" /> {t("sefUlazne.trazimNaSefu")}
                </div>
            )}

            {/* Zašto se faktura ne može menjati u celini — razlog daje procedura. */}
            {forma.id && nivo.Razlog && (
                <div className={`ulazne-nivo-traka nivo-${nivo.Nivo.toLowerCase()}`}>
                    <i className={samoPregled ? "pi pi-lock" : "pi pi-info-circle"} /> {t("ulazne.nivo" + nivo.Nivo)}: {nivo.Razlog}
                </div>
            )}

            {/* Skrol pripada akordeonu, ne celoj formi — tabela stavki i dugme
                za snimanje ostaju vidljivi (isto kao na izlaznim fakturama). */}
            <div className="ulazne-akordeon">
            <Accordion multiple activeIndex={[0]}>
                {/* ─────────── osnovni podaci ───────────
                    Tri reda: ko i šta (broj, dobavljač, grupa), kada (datumi i rok),
                    i za deviznu valuta i kurs. Iznosi su zbir stavki i stoje
                    zaključani — papir i stavke ne smeju da se raziđu. */}
                <AccordionTab header={t("nova.osnovniPodaci")}>
                    <div className="gridFakture ulazne-grid">
                        <div className="col-12 md:col-3">
                            <label htmlFor="fakturaBr">{t("nova.fakturaBr")}:</label>
                            <InputText id="fakturaBr" readOnly value={forma.fakturaBr}
                                className="ulazne-polje-zakljucano" />
                        </div>

                        <div className="col-12 md:col-3">
                            <label htmlFor="refNo">{t("ulazne.brojFakture")}: <span className="iz-obavezno">*</span></label>
                            <InputText id="refNo" disabled={zakljucano("refNo")} value={forma.refNo} required
                                className={nevalja(forma.refNo) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("refNo", e.target.value)} />
                        </div>

                        <div className="polje-siroko col-12 md:col-6">
                            <label htmlFor="id_klijent">{t("ulazne.dobavljac")}: <span className="iz-obavezno">*</span></label>
                            <CustomDropdown id="id_klijent" disabled={zakljucano("id_klijent")} name="id_klijent" optionLabel="label"
                                required value={forma.id_klijent}
                                invalid={nevalja(forma.id_klijent)}
                                options={sifarnici.DobavljaciDB || []}
                                placeholder={t("ulazne.phDobavljac")}
                                onChange={(e) => izmeni("id_klijent", e.target.value)} />
                        </div>

                        <div className="col-12 md:col-3">
                            <label htmlFor="datum">{t("ulazne.datumFakture")}: <span className="iz-obavezno">*</span></label>
                            <Calendar id="datum" disabled={zakljucano("datum")} value={forma.datum} showIcon required
                                dateFormat="dd.mm.yy" mask="99.99.9999"
                                className={nevalja(forma.datum) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("datum", e.value)} />
                        </div>

                        {/* Datum prometa (SEF: datum prometa dobara/usluga) određuje
                            PDV period u kom se koristi prethodni porez. */}
                        <div className="col-12 md:col-3">
                            <label htmlFor="datumPrometa">{t("ulazne.datumPrometa")}: <span className="iz-obavezno">*</span></label>
                            <Calendar id="datumPrometa" disabled={zakljucano("datumPrometa")} value={forma.datumPrometa} showIcon required
                                dateFormat="dd.mm.yy" mask="99.99.9999"
                                className={nevalja(forma.datumPrometa) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("datumPrometa", e.value)} />
                        </div>

                        <div className="col-12 md:col-2">
                            <label htmlFor="datumPrijema">{t("ulazne.datumPrijema")}:</label>
                            <Calendar id="datumPrijema" disabled={zakljucano("datumPrijema")} value={forma.datumPrijema} showIcon
                                dateFormat="dd.mm.yy" mask="99.99.9999"
                                onChange={(e) => izmeni("datumPrijema", e.value)} />
                        </div>

                        <div className="col-12 md:col-2">
                            <label htmlFor="rokDana">{t("ulazne.rokDana")}:</label>
                            <InputNumber id="rokDana" disabled={zakljucano("rokDana")} value={forma.rokDana} useGrouping={false}
                                onValueChange={(e) => izmeni("rokDana", e.value)} />
                        </div>

                        <div className="col-12 md:col-2">
                            <label htmlFor="rokPlacanjaDatum">{t("ulazne.rokPlacanja")}:</label>
                            <Calendar id="rokPlacanjaDatum" disabled={zakljucano("rokPlacanjaDatum")} value={forma.rokPlacanjaDatum} showIcon
                                dateFormat="dd.mm.yy" mask="99.99.9999"
                                onChange={(e) => izmeni("rokPlacanjaDatum", e.value)} />
                        </div>

                        <div className="col-12 md:col-3">
                            <label htmlFor="id_grupa_fakture">{t("ulazne.grupa")}: <span className="iz-obavezno">*</span></label>
                            <CustomDropdown id="id_grupa_fakture" disabled={zakljucano("id_grupa_fakture")} name="id_grupa_fakture" optionLabel="label"
                                required value={forma.id_grupa_fakture}
                                invalid={nevalja(forma.id_grupa_fakture)}
                                options={sifarnici.GrupeDB || []}
                                placeholder={t("ulazne.phGrupa")}
                                onChange={(e) => izmeni("id_grupa_fakture", e.target.value)} />
                        </div>

                        <div className="col-12 md:col-3">
                            <label htmlFor="statusUlazne">{t("ulazne.status")}: <span className="iz-obavezno">*</span></label>
                            <CustomDropdown id="statusUlazne" disabled={zakljucano("statusUlazne")} name="statusUlazne" optionLabel="label"
                                value={forma.statusUlazne} invalid={nevalja(forma.statusUlazne)}
                                options={sifarnici.StatusiDB || []}
                                placeholder={t("ulazne.phStatus")}
                                onChange={(e) => izmeni("statusUlazne", e.target.value)} />
                        </div>


                        <div className="col-12 md:col-3">
                            <label htmlFor="podpozicija">{t("ulazne.podpozicija")}:</label>
                            <InputText id="podpozicija" disabled={zakljucano("podpozicija")} value={forma.podpozicija || ""}
                                maxLength={200} placeholder={t("ulazne.phPodpozicija")}
                                onChange={(e) => izmeni("podpozicija", e.target.value)} />
                        </div>

                        {devizna && (
                            <>
                                <div className="col-12 md:col-2">
                                    <label htmlFor="valuta">{t("ulazne.valuta")}: <span className="iz-obavezno">*</span></label>
                                    <CustomDropdown id="valuta" disabled={zakljucano("valuta")} name="valuta" optionLabel="label"
                                        value={forma.valuta} options={(sifarnici.ValuteDB || []).filter((v) => v.label !== "RSD")}
                                        invalid={nevalja(forma.valuta)}
                                        placeholder={t("ulazne.phValuta")}
                                        onChange={(e) => izmeni("valuta", e.target.value)} />
                                </div>
                                <div className="col-12 md:col-2">
                                    <label htmlFor="kurs">{t("ulazne.kurs")}: <span className="iz-obavezno">*</span></label>
                                    <InputNumber id="kurs" value={forma.kurs} mode="decimal"
                                        minFractionDigits={4} maxFractionDigits={4}
                                        invalid={nevalja(forma.kurs)}
                                        disabled={zakljucano("kurs")}
                                        onValueChange={(e) => { kursRucno.current = true; izmeni("kurs", e.value); }} />
                                </div>
                            </>
                        )}

                        {/* Razlog se pojavljuje TEK kad je faktura odbijena — inače stoji
                            prazno polje koje niko ne popunjava. */}
                        {jeOdbijen && (
                            <div className="polje-puno col-12">
                                <label htmlFor="napomenaStatusa">{t("ulazne.razlogOdbijanja")}: <span className="iz-obavezno">*</span></label>
                                <InputTextarea id="napomenaStatusa" disabled={zakljucano("napomenaStatusa")} rows={2} value={forma.napomenaStatusa}
                                    className={poslato && !String(forma.napomenaStatusa || "").trim() ? "p-invalid" : ""}
                                    onChange={(e) => izmeni("napomenaStatusa", e.target.value)} />
                            </div>
                        )}

                        <div className="polje-puno col-12 md:col-6">
                            <label htmlFor="napomena">{t("nova.napomena")}:</label>
                            <InputTextarea id="napomena" disabled={zakljucano("napomena")} rows={2} value={forma.napomena}
                                onChange={(e) => izmeni("napomena", e.target.value)} />
                        </div>

                        {/* Iznosi = zbir stavki (dok stavki nema — iznos sa SEF-a). */}
                        <div className="ulazne-red-pun ulazne-iznosi">
                            <span className="ulazne-iznosi-naslov">{t("ulazne.iznosiZbirStavki")}</span>
                            {[["neto", forma.netoIznos], ["pdv", forma.pdvIznos], ["bruto", forma.brutoIznos]].map(([k, v]) => (
                                <span key={k} className="ulazne-iznos">
                                    <span className="ulazne-iznos-oznaka">{t("ulazne." + k)}</span>
                                    <span className="ulazne-iznos-vrednost">
                                        {Number(v || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </span>
                            ))}
                            {razlikaSef !== null && (
                                <span className="ulazne-iznos-razlika">
                                    <i className="pi pi-exclamation-triangle" /> {t("ulazne.razlikaSef", {
                                        razlika: razlikaSef.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                                </span>
                            )}
                        </div>
                    </div>
                </AccordionTab>

                {/* ─────────── plaćanje ───────────
                    Račun i poziv na broj dobavljača — to je ono što ide na nalog za
                    prenos. Kod SEF fakture stižu iz dokumenta. Naš račun se bira
                    pri plaćanju, ne pri unosu. */}
                <AccordionTab header={t("ulazne.placanje")}>
                    <div className="gridFakture ulazne-grid">
                        <div className="polje-siroko col-12 md:col-4">
                            <label htmlFor="racunDobavljaca">{t("ulazne.racunDobavljaca")}:</label>
                            <InputText id="racunDobavljaca" disabled={zakljucano("racunDobavljaca")} value={forma.racunDobavljaca}
                                placeholder="160-0000000000000-00"
                                onChange={(e) => izmeni("racunDobavljaca", e.target.value)} />
                        </div>
                        <div className="col-12 md:col-2">
                            <label htmlFor="modelOdobrenja">{t("ulazne.model")}:</label>
                            <InputText id="modelOdobrenja" disabled={zakljucano("modelOdobrenja")} value={forma.modelOdobrenja} maxLength={2}
                                onChange={(e) => izmeni("modelOdobrenja", e.target.value.replace(/\D/g, ""))} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label htmlFor="pozivNaBrojOdobrenja">{t("ulazne.pozivOdobrenja")}:</label>
                            <InputText id="pozivNaBrojOdobrenja" disabled={zakljucano("pozivNaBrojOdobrenja")} value={forma.pozivNaBrojOdobrenja}
                                onChange={(e) => izmeni("pozivNaBrojOdobrenja", e.target.value)} />
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
            </div>


            {/* ─────────── donja traka ───────────
                Ostale tri kartice su za sada samo prikaz tabele — struktura je tu
                da se kasnije popuni, a operater odmah vidi gde šta ide. */}
            <div className="ulazne-donji-deo">
                {/* Poruka o zaključanim stavkama stoji uz tabove, desno od
                    „Povezane izlazne fakture" — razlog zašto se stavke ne mogu
                    unositi vidi se sa svakog taba, ne tek kad se otvore Stavke. */}
                <div className="ulazne-tabovi-red">
                    <TabMenu id="modelTabMagacin" className="ulazne-trakica"
                        activeIndex={donjiTab} onTabChange={(e) => setDonjiTab(e.index)}
                        model={[
                            { label: t('ulazne.stavke') },
                            { label: t('ulazne.placanja') },
                            { label: t('ulazne.povezaniNalozi') },
                            { label: t('ulazne.koKz') },
                        ]} />
                    {stavkeZakljucane && (
                        <span className="ulazne-stavke-zakljucano">
                            <i className="pi pi-lock" /> {t('sefUlazne.stavkeZakljucane')}
                        </span>
                    )}
                </div>

                {donjiTab === 0 && (
                    <>
                        {sefPredlog?.length > 0 && !stavke.length && !dodavanjeZabranjeno && (
                            <div className="ulazne-sef-predlog">
                                <div className="ulazne-sef-predlog-zaglavlje">
                                    <span><i className="pi pi-cloud-download" /> {t("ulazne.sefStavkeNaslov", { broj: sefPredlog.length })}</span>
                                    <span className="ulazne-sef-predlog-sve">
                                        {t("ulazne.sefStavkeZaSve")}:
                                        <CustomDropdown name="predlogVrsta" optionLabel="label" value={predlogSve.id_vrsta_troska}
                                            options={sifarnici.VrsteTroskaDB || []} placeholder={t("ulazne.phVrsta")}
                                            onChange={(e) => primeniNaSve("id_vrsta_troska", e.target.value)} />
                                        <CustomDropdown name="predlogPodvrsta" optionLabel="label" value={predlogSve.id_podvrsta_troska}
                                            options={podvrsteZa(predlogSve.id_vrsta_troska)} disabled={!predlogSve.id_vrsta_troska}
                                            placeholder={t("ulazne.phPodvrsta")}
                                            onChange={(e) => primeniNaSve("id_podvrsta_troska", e.target.value)} />
                                    </span>
                                    <Button type="button" icon="pi pi-check" label={t("ulazne.sefStavkeUpisi")}
                                        severity="success" className="p-button-sm" loading={predlogSalje}
                                        disabled={!forma.id} onClick={upisiSefStavke} />
                                </div>
                                {!forma.id && <div className="ulazne-sef-napomena">{t("ulazne.sefStavkePrvoSacuvaj")}</div>}
                                <DataTable value={sefPredlog} className="izlazneFaktureTable stavke-tabela"
                                    showGridlines stripedRows size="small" dataKey="rb">
                                    <Column field="rb" header={t("ulazne.rb")} style={{ width: "50px" }} />
                                    <Column field="opis" header={t("ulazne.opis")} style={{ minWidth: "200px" }} />
                                    <Column header={t("ulazne.kolicina")} body={(r) => `${r.kolicina ?? ""} ${r.jedinicaMere || ""}`} />
                                    <Column header={t("ulazne.neto")} body={(r) => Number(r.neto || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                                    <Column field="stopaPDV" header={t("ulazne.stopaPDV")} />
                                    <Column header={t("ulazne.vrstaTroska")} style={{ minWidth: "180px" }}
                                        body={(r) => <CustomDropdown name={"pv" + r.rb} optionLabel="label" value={r.id_vrsta_troska}
                                            options={sifarnici.VrsteTroskaDB || []} placeholder={t("ulazne.phVrsta")}
                                            onChange={(e) => izmeniPredlog(r.rb, "id_vrsta_troska", e.target.value)} />} />
                                    <Column header={t("ulazne.podvrstaTroska")} style={{ minWidth: "180px" }}
                                        body={(r) => <CustomDropdown name={"pp" + r.rb} optionLabel="label" value={r.id_podvrsta_troska}
                                            options={podvrsteZa(r.id_vrsta_troska)} disabled={!r.id_vrsta_troska}
                                            placeholder={t("ulazne.phPodvrsta")}
                                            onChange={(e) => izmeniPredlog(r.rb, "id_podvrsta_troska", e.target.value)} />} />
                                    <Column header={t("ulazne.povratPDV")} style={{ width: "90px" }}
                                        body={(r) => <Checkbox checked={!!r.povratPDV}
                                            onChange={(e) => izmeniPredlog(r.rb, "povratPDV", e.checked)} />} />
                                </DataTable>
                            </div>
                        )}
                        <Tooltip target=".export-buttons>button" position="bottom" />
                        <DataTable value={stavke} emptyMessage={t("common.noDataTable")}
                            className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                            scrollable scrollHeight="32vh"
                            globalFilter={filterStavki} header={zaglavljeStavki}
                            selectionMode="single" selection={izabranaStavka}
                            onSelectionChange={(e) => setIzabranaStavka(e.value)}
                            onRowDoubleClick={(e) => !izmenaZabranjena && otvoriStavku(e.data)}>
                            <Column field="rb" header={t("ulazne.rb")} style={{ width: "60px" }} />
                            <Column header={t("ulazne.vrstaTroska")}
                                body={(r) => r.VrstaTroskaNaziv || nazivIz("VrsteTroskaDB", r.id_vrsta_troska)} />
                            <Column header={t("ulazne.podvrstaTroska")}
                                body={(r) => r.PodvrstaTroskaNaziv || nazivIz("PodvrsteTroskaDB", r.id_podvrsta_troska)} />
                            {jeMagacin && <Column header={t("ulazne.artikal")}
                                body={(r) => nazivIz("ArtikliDB", r.id_artikal)} />}
                            {jeMagacin && <Column field="kolicina" header={t("ulazne.kolicina")} />}
                            {jeMagacin && <Column field="jedinicnaCena" header={t("ulazne.jedinicnaCena")} />}
                            <Column header={t("ulazne.vozilo")}
                                body={(r) => nazivIz("VozilaDB", r.id_vozilo)} />
                            <Column field="netoIznos" header={t("ulazne.neto")} />
                            <Column field="stopaPDV" header={t("ulazne.stopaPDV")} />
                            <Column field="pdvIznos" header={t("ulazne.pdv")} />
                            <Column field="brutoIznos" header={t("ulazne.bruto")} />
                        </DataTable>
                    </>
                )}

                {donjiTab === 1 && (
                    /* Plaćanja = sravnjenja sa izvodom; unose se na izvodu, ovde se samo vide. */
                    <DataTable value={placanja} emptyMessage={t("common.noDataTable")}
                        className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                        globalFilter={filterOstalo} header={zaglavljeProsto(setFilterOstalo)}
                        scrollable scrollHeight="32vh">
                        <Column field="DatumIzvoda" header={t("ulazne.datumUplate")} />
                        <Column field="BrojIzvoda" header={t("izvod.broj")} />
                        <Column field="Racun" header={t("ulazne.tekuciRacun")} />
                        <Column header={t("ulazne.iznos")}
                            body={(r) => Number(r.Iznos || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                        <Column field="Napomena" header={t("nova.napomena")} />
                    </DataTable>
                )}

                {donjiTab === 2 && !spec && (
                    /* Stavke vezane za radni nalog. Radnje su u zaglavlju tabele, isto
                       kao na Stavkama i KO / KZ: povezivanje (dijalog), raskid veze
                       izabranog reda i čitanje PDF specifikacije. */
                    <>
                        <Tooltip target=".export-buttons>button" position="bottom" />
                        <DataTable value={veze} dataKey="id"
                            emptyMessage={forma.id ? t("ulazne.nemaVezaSaNalozima") : t("ulazne.prvoSacuvajFakturu")}
                            className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                            scrollable scrollHeight="32vh" globalFilter={filterNaloga}
                            selectionMode="single" selection={izabranaVeza}
                            onSelectionChange={(e) => setIzabranaVeza(e.value)}
                            header={(
                                <div className="export-buttons fakture-stavke-header">
                                    <div className="fakture-stavke-radnje">
                                        <Button type="button" icon="pi pi-link" severity="info" rounded
                                            disabled={!forma.id || samoPregled || !stavke.length}
                                            onClick={() => { setVeza({ id_stavka: null, id_radni_nalog: null }); setVezaDialog(true); }}
                                            data-pr-tooltip={t("ulazne.poveziStavkuSaNalogom")} />
                                        <Button type="button" icon="pi pi-minus" severity="danger" rounded
                                            disabled={!izabranaVeza || samoPregled}
                                            onClick={() => izabranaVeza && veziNalog(izabranaVeza.id, null)}
                                            data-pr-tooltip={t("ulazne.raskiniVezu")} />
                                        <Button type="button" icon="pi pi-file-pdf" severity="secondary" rounded
                                            loading={specUcitava} disabled={!forma.id || dodavanjeZabranjeno}
                                            onClick={() => specFajlRef.current?.click()}
                                            data-pr-tooltip={t("ulazne.prilozSpecifikaciju")} />
                                        <input ref={specFajlRef} type="file" accept="application/pdf,.pdf"
                                            style={{ display: "none" }} onChange={ucitajSpecifikaciju} />
                                    </div>
                                    <input type="search" placeholder={t('nova.phPretraga')}
                                        className="p-inputtext p-component"
                                        onInput={(e) => setFilterNaloga(e.target.value)} />
                                </div>
                            )}>
                            <Column field="rb" header={t("ulazne.rb")} style={{ width: "60px" }} />
                            <Column field="Podvrsta" header={t("ulazne.podvrstaTroska")} style={{ minWidth: "160px" }} />
                            <Column header={t("ulazne.bruto")} style={{ width: "130px" }}
                                body={(r) => <span className="sef-iznos">{Number(r.BrutoIznos || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>} />
                            <Column header={t("ulazne.radniNalog")} style={{ minWidth: "140px" }}
                                body={(r) => `RN ${r.BrojRadnogNaloga}${r.BrojPutnogNaloga ? " · PN " + r.BrojPutnogNaloga : ""}`} />
                            <Column header={t("ulazne.vozilo")} style={{ minWidth: "160px" }}
                                body={(r) => [r.Vozilo, r.Prikolica].filter(Boolean).join(" + ")} />
                            <Column field="Vozac" header={t("ulazne.vozac")} style={{ minWidth: "160px" }} />
                            <Column field="Klijent" header={t("ulazne.klijentNaloga")} style={{ minWidth: "180px" }} />
                            <Column field="Polazak" header={t("ulazne.polazak")} style={{ width: "110px" }} />
                        </DataTable>
                    </>
                )}

                {donjiTab === 2 && spec && (
                    /* Pregled pročitane PDF specifikacije — zamenjuje tabelu veza dok se
                       ne sačuva ili odustane, da na ekranu nisu dve tabele jedna ispod druge. */
                    <>
                        {spec?.neprepoznati?.length > 0 && (
                            <div className="sef-ulazne-napomena sef-napomena-upozorenje">
                                <i className="pi pi-exclamation-triangle" />{" "}
                                {t("ulazne.specNeprepoznati", { broj: spec.neprepoznati.length })}
                            </div>
                        )}
                        <Tooltip target=".export-buttons>button" position="bottom" />
                        <DataTable value={spec?.stavke || []} emptyMessage={t("ulazne.specNemaStavki")}
                            className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                            scrollable scrollHeight="32vh"
                            rowClassName={(r) => ({
                                "iz-red-sravnjen": r.Jacina === "OBA_VOZILA",
                                "iz-red-dospela": !r.id_radni_nalog,
                            })}
                            header={(
                                <div className="export-buttons fakture-stavke-header">
                                    <div className="fakture-stavke-radnje">
                                        <Button type="button" icon="pi pi-check" severity="success" rounded
                                            loading={specUcitava} disabled={dodavanjeZabranjeno}
                                            onClick={sacuvajSpecifikaciju}
                                            data-pr-tooltip={t("ulazne.specSacuvaj")} />
                                        <Button type="button" icon="pi pi-times" severity="secondary" rounded outlined
                                            onClick={() => { setSpec(null); setPotvrdjene({}); }}
                                            data-pr-tooltip={t("common.cancel")} />
                                    </div>
                                    <span className="ulazne-spec-sazetak">
                                        {t("ulazne.specSazetak", {
                                            broj: spec.stavke?.length || 0,
                                            ukupno: Number(spec.ukupno || 0).toLocaleString("sr-RS",
                                                { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                                        })}
                                    </span>
                                </div>
                            )}>
                            <Column header={t("ulazne.specPotvrdi")} style={{ width: "80px" }}
                                body={(r) => (
                                    <Checkbox checked={!!potvrdjene[r.bid]}
                                        disabled={!r.id_radni_nalog || dodavanjeZabranjeno}
                                        onChange={(e) => setPotvrdjene((p) => ({ ...p, [r.bid]: e.checked }))} />
                                )} />
                            <Column field="bid" header="Bid" style={{ minWidth: "90px" }} />
                            <Column field="vucnoVozilo" header={t("ulazne.vucnoVozilo")} style={{ minWidth: "110px" }} />
                            <Column field="prikljucnoVozilo" header={t("ulazne.prikljucnoVozilo")} style={{ minWidth: "120px" }}
                                body={(r) => r.prikljucnoVozilo || "—"} />
                            <Column header={t("ulazne.vremeUlaska")} style={{ minWidth: "150px" }}
                                body={(r) => r.datumUlaska
                                    ? new Date(r.datumUlaska).toLocaleString("sr-RS",
                                        { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                                    : "—"} />
                            <Column header={t("ulazne.iznos")} style={{ minWidth: "110px" }}
                                body={(r) => <span className="sef-iznos">
                                    {Number(r.iznos || 0).toLocaleString("sr-RS",
                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>} />
                            <Column header={t("ulazne.radniNalog")} style={{ minWidth: "160px" }}
                                body={(r) => r.BrojRadnogNaloga
                                    ? `${r.BrojRadnogNaloga}${r.Vozac ? " · " + r.Vozac : ""}`
                                    : "—"} />
                            <Column header={t("ulazne.nalaz")} style={{ minWidth: "140px" }}
                                body={(r) => (
                                    <span className={`sef-pilula ${
                                        r.Jacina === "OBA_VOZILA" ? "sef-st-ok"
                                            : r.Jacina === "VUCNO" ? "sef-st-upozorenje"
                                            : "sef-st-kritican"}`}>
                                        {t("ulazne.nalaz" + r.Jacina)}
                                    </span>
                                )} />
                        </DataTable>
                    </>
                )}

                {donjiTab === 3 && (
                    /* Knjižna odobrenja (umanjuju dug) i zaduženja dobavljača vezana za
                       ovu fakturu. Unose se kao poseban dokument (KO/KZ). */
                    <DataTable value={kokz} emptyMessage={t("common.noDataTable")}
                        className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                        globalFilter={filterOstalo}
                        header={(
                            <div className="export-buttons fakture-stavke-header">
                                <div className="fakture-stavke-radnje">
                                    <Button type="button" icon="pi pi-plus" severity="info" rounded
                                        disabled={!forma.id || samoPregled || !naKoKz}
                                        onClick={() => naKoKz({ zaFakturu: { id_faktura: forma.id, id_klijent: forma.id_klijent } })}
                                        tooltip={t("ulazne.noviKoKz")} tooltipOptions={{ position: "bottom" }} />
                                </div>
                                <input type="search" placeholder={t('nova.phPretraga')}
                                    className="p-inputtext p-component"
                                    onInput={(e) => setFilterOstalo(e.target.value)} />
                            </div>
                        )}
                        scrollable scrollHeight="32vh">
                        <Column style={{ width: "90px" }}
                            body={(r) => (
                                <Button label={t("common.choose")} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" disabled={!naKoKz}
                                    onClick={() => naKoKz({ otvori: r.id })} />
                            )} />
                        <Column field="Tip" header={t("nova.tip")} style={{ width: "70px" }} />
                        <Column field="BrojDobavljaca" header={t("ulazne.brojFakture")} />
                        <Column field="Datum" header={t("nova.datum")} />
                        <Column header={t("ulazne.iznos")}
                            body={(r) => Number(r.Iznos || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                        <Column field="Napomena" header={t("nova.napomena")} />
                    </DataTable>
                )}
            </div>
            {!samoPregled && <div className="formUnosZap buttonUnosZap">
                <button type="button" onClick={sacuvaj} disabled={salje}
                    className="btn btn-success">
                    {forma.id ? t("common.save") : t("admin.unos")}
                </button>
            </div>}

            {/* ─────────── potvrda SEF odluke ───────────
                Ista potvrda kao na ekranu „Pregled sa SEF-a": broj, dobavljač i
                iznos stoje u samom pitanju, jer SEF odluku ne dozvoljava da se
                promeni. Generičko „Da li ste sigurni?" ovde ne vredi ništa. */}
            <Dialog
                header={sefPotvrda?.prihvacen ? t("sefUlazne.potvrdaPrihvati") : t("sefUlazne.potvrdaOdbij")}
                visible={!!sefPotvrda} style={{ width: "34rem" }}
                breakpoints={{ "768px": "95vw" }}
                onHide={() => { setSefPotvrda(null); setSefRazlog(""); }}>
                {sefDok && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-pitanje">
                            {t(sefPotvrda?.prihvacen ? "sefUlazne.pitanjePrihvati" : "sefUlazne.pitanjeOdbij", {
                                broj: sefDok.DocumentNumber,
                                dobavljac: sefDok.SupplierName,
                                iznos: `${Number(sefDok.Amount || 0).toLocaleString("sr-RS", {
                                    minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sefDok.Currency || ""}`,
                            })}
                        </p>
                        <p className="sef-potvrda-upozorenje">
                            <i className="pi pi-exclamation-triangle" /> {t("sefUlazne.nepovratno")}
                        </p>
                        <label htmlFor="sefRazlogForma">
                            {sefPotvrda?.prihvacen ? t("sefUlazne.napomenaOpciono") : t("ulazne.razlogOdbijanja")}:
                        </label>
                        <InputTextarea id="sefRazlogForma" rows={3} value={sefRazlog}
                            style={{ width: "100%" }}
                            onChange={(e) => setSefRazlog(e.target.value)}
                            placeholder={sefPotvrda?.prihvacen ? "" : t("sefUlazne.phRazlog")} />
                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => { setSefPotvrda(null); setSefRazlog(""); }} />
                            <Button type="button"
                                label={sefPotvrda?.prihvacen ? t("sefUlazne.prihvati") : t("sefUlazne.odbij")}
                                severity={sefPotvrda?.prihvacen ? "success" : "danger"}
                                /* Odbijanje bez razloga ne prolazi ni ovde ni u proceduri. */
                                disabled={!sefPotvrda?.prihvacen && !sefRazlog.trim()}
                                loading={sefSalje} onClick={potvrdiSefOdluku} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ─────────── prilozi uz fakturu ─────────── */}
            <Dialog header={t("sefUlazne.priloziNaslov")} visible={priloziDialog !== null}
                style={{ width: "44rem" }} breakpoints={{ "768px": "95vw" }}
                onHide={() => setPriloziDialog(null)}>
                {priloziDialog?.ucitava && (
                    <div className="sef-ulazne-napomena">
                        <i className="pi pi-spin pi-spinner" /> {t("common.ucitavanje")}
                    </div>
                )}
                {priloziDialog && !priloziDialog.ucitava && priloziDialog.spisak.length === 0 && (
                    <div className="sef-ulazne-napomena">
                        <i className="pi pi-info-circle" /> {t("sefUlazne.nemaPriloga")}
                    </div>
                )}
                {priloziDialog && !priloziDialog.ucitava && priloziDialog.spisak.map((p) => (
                    <div className="sef-prilog-red" key={p.redni}>
                        <i className="pi pi-file" />
                        <span className="sef-prilog-ime" title={p.naziv}>{p.naziv}</span>
                        <span className="sef-prilog-velicina">{p.velicinaKb} KB</span>
                        <Button type="button" icon="pi pi-eye" rounded outlined className="p-button-sm"
                            onClick={() => otvoriPrilog(p, false)} />
                        <Button type="button" icon="pi pi-download" rounded outlined className="p-button-sm"
                            onClick={() => otvoriPrilog(p, true)} />
                    </div>
                ))}
            </Dialog>

            {/* ─────────── modal stavke ───────────
                Osnovno je ono što je CYCLE stvarno popunjavao: vrsta, podvrsta,
                neto, PDV i povrat PDV-a. Raspodela troška (vozilo, zaposleni, KM)
                je zatvorena dok se ne zatraži, a artikal/magacin/količina postoje
                SAMO na fakturi grupe Magacin — na ostalim su bili prazni u 100%. */}
            <Dialog header={t("ulazne.stavkaNaslov")} visible={stavkaVidljiva}
                style={{ width: "60vw" }} breakpoints={{ "1200px": "80vw", "768px": "95vw" }}
                onHide={() => setStavkaVidljiva(false)}>
                <div className="gridFakture ulazne-grid">
                    <div className="col-12 md:col-4">
                        <label>{t("ulazne.vrstaTroska")}: <span className="iz-obavezno">*</span></label>
                        {saDodavanjem(
                            <CustomDropdown name="id_vrsta_troska" optionLabel="label"
                                value={stavka.id_vrsta_troska} options={sifarnici.VrsteTroskaDB || []}
                                invalid={stavkaPoslata && !stavka.id_vrsta_troska}
                                placeholder={t("ulazne.phVrsta")}
                                onChange={(e) => {
                                    /* Promenom vrste podvrsta više ne mora da važi. */
                                    setStavka((p) => ({ ...p, id_vrsta_troska: e.target.value, id_podvrsta_troska: null }));
                                }} />,
                            () => otvoriSifarnik("VRSTA"),
                            stavka.id_vrsta_troska ? () => otvoriSifarnik("VRSTA", stavka.id_vrsta_troska) : null)}
                    </div>

                    <div className="col-12 md:col-4">
                        <label>{t("ulazne.podvrstaTroska")}: <span className="iz-obavezno">*</span></label>
                        {saDodavanjem(
                            <CustomDropdown name="id_podvrsta_troska" optionLabel="label"
                                value={stavka.id_podvrsta_troska} options={podvrsteZaVrstu}
                                invalid={stavkaPoslata && !stavka.id_podvrsta_troska}
                                disabled={!stavka.id_vrsta_troska}
                                placeholder={t("ulazne.phPodvrsta")}
                                onChange={(e) => izaberiPodvrstu(e.target.value)} />,
                            () => {
                                if (!stavka.id_vrsta_troska) {
                                    poruka("warn", t("common.upozorenje"), t("ulazne.prvoVrsta"));
                                    return;
                                }
                                otvoriSifarnik("PODVRSTA");
                            },
                            stavka.id_podvrsta_troska ? () => otvoriSifarnik("PODVRSTA", stavka.id_podvrsta_troska) : null)}
                    </div>


                    {/* ─── artikal: na svakoj grupi, nije obavezan (CYCLE ga nudi uvek) ─── */}
                    <div className="col-12 md:col-6">
                        <label>{t("ulazne.artikal")}:</label>
                        {saDodavanjem(
                            <CustomDropdown name="id_artikal" optionLabel="label" showClear
                                value={stavka.id_artikal} options={sifarnici.ArtikliDB || []}
                                placeholder={t("ulazne.phArtikal")}
                                onChange={(e) => izaberiArtikal(e.target.value)} />,
                            () => setArtikalModal(true))}
                    </div>

                    {/* ─── magacin: samo grupa Magacin ─── */}
                    {jeMagacin && !samoRaspodela && (
                        <>
                            <div className="ulazne-red-pun ulazne-podnaslov">{t("ulazne.magacinPodaci")}</div>
                            <div className="col-12 md:col-6">
                                <label>{t("ulazne.magacin")}:</label>
                                <CustomDropdown name="id_magacin" optionLabel="label"
                                    value={stavka.id_magacin} options={sifarnici.MagacinDB || []}
                                    placeholder={t("ulazne.phMagacin")}
                                    onChange={(e) => izmeniStavku("id_magacin", e.target.value)} />
                            </div>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.kolicina")}:</label>
                                <InputNumber value={stavka.kolicina} mode="decimal" minFractionDigits={0}
                                    maxFractionDigits={3}
                                    onValueChange={(e) => izmeniStavku("kolicina", e.value)} />
                            </div>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.jedinicaMere")}:</label>
                                <CustomDropdown name="jedinicaMere" optionLabel="label"
                                    value={stavka.jedinicaMere} options={sifarnici.JediniceMereDB || []}
                                    placeholder={t("ulazne.phJedinica")}
                                    onChange={(e) => izmeniStavku("jedinicaMere", e.target.value)} />
                            </div>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.jedinicnaCena")}:</label>
                                <InputNumber value={stavka.jedinicnaCena} mode="decimal" minFractionDigits={2}
                                    maxFractionDigits={4}
                                    onValueChange={(e) => izmeniStavku("jedinicnaCena", e.value)} />
                            </div>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.rabat")}:</label>
                                <InputNumber value={stavka.rabat} mode="decimal" minFractionDigits={2}
                                    suffix=" %"
                                    onValueChange={(e) => izmeniStavku("rabat", e.value)} />
                            </div>
                        </>
                    )}

                    {/* ─── iznosi: neto se unosi, PDV i bruto računaju ─── */}
                    <div className="col-12 md:col-3">
                        <label>{t("ulazne.neto")}: <span className="iz-obavezno">*</span></label>
                        <InputNumber value={stavka.netoIznos} mode="decimal" minFractionDigits={2}
                            className={stavkaPoslata && !stavka.netoIznos ? "p-invalid" : ""}
                            disabled={samoRaspodela}
                            readOnly={jeMagacin && !!stavka.kolicina && !!stavka.jedinicnaCena}
                            onValueChange={(e) => izmeniIznos("netoIznos", e.value)} />
                    </div>
                    <div className="col-12 md:col-2">
                        <label>{t("ulazne.stopaPDV")}: <span className="iz-obavezno">*</span></label>
                        <InputNumber value={stavka.stopaPDV} mode="decimal" minFractionDigits={0}
                            className={stavkaPoslata && stavka.stopaPDV == null ? "p-invalid" : ""}
                            maxFractionDigits={2} suffix=" %" disabled={samoRaspodela}
                            onValueChange={(e) => izmeniIznos("stopaPDV", e.value)} />
                    </div>
                    <div className="col-12 md:col-2">
                        <label>{t("ulazne.pdv")}:</label>
                        <InputNumber value={stavka.pdvIznos} mode="decimal" minFractionDigits={2}
                            disabled={samoRaspodela}
                            onValueChange={(e) => izmeniIznos("pdvIznos", e.value)} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label>{t("ulazne.bruto")}: <span className="iz-obavezno">*</span></label>
                        <InputNumber value={stavka.brutoIznos} mode="decimal" minFractionDigits={2}
                            readOnly inputClassName="ulazne-polje-zakljucano" />
                    </div>
                    <div className="col-12 md:col-2 ulazne-kvacica" style={{ alignSelf: "end" }}>
                        <Checkbox inputId="povratPDV" checked={!!stavka.povratPDV} disabled={samoRaspodela}
                            onChange={(e) => izmeniStavku("povratPDV", e.checked)} />
                        <label htmlFor="povratPDV">{t("ulazne.povratPDV")}</label>
                    </div>

                    <div className="col-12">
                        <label>{t("ulazne.opis")}:</label>
                        <InputTextarea rows={2} value={stavka.opis}
                            onChange={(e) => izmeniStavku("opis", e.target.value)} />
                    </div>

                    {/* ─── raspodela troška: zatvoreno dok se ne zatraži ─── */}
                    <div className="ulazne-red-pun">
                        <Button type="button" className="p-button-text p-button-sm ulazne-raspodela-dugme"
                            icon={raspodelaOtvorena ? "pi pi-chevron-down" : "pi pi-chevron-right"}
                            label={t("ulazne.raspodelaTroska")}
                            onClick={() => setRaspodelaOtvorena((x) => !x)} />
                    </div>
                    {raspodelaOtvorena && (
                        <>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.vozilo")}:</label>
                                <CustomDropdown name="id_vozilo" optionLabel="label"
                                    value={stavka.id_vozilo} options={sifarnici.VozilaDB || []}
                                    placeholder={t("ulazne.phVozilo")}
                                    onChange={(e) => izmeniStavku("id_vozilo", e.target.value)} />
                            </div>
                            <div className="col-12 md:col-3">
                                <label>{t("ulazne.zaposleni")}:</label>
                                <CustomDropdown name="id_zaposleni" optionLabel="label"
                                    value={stavka.id_zaposleni} options={sifarnici.ZaposleniDB || []}
                                    placeholder={t("ulazne.phZaposleni")}
                                    onChange={(e) => izmeniStavku("id_zaposleni", e.target.value)} />
                            </div>
                            <div className="col-12 md:col-2">
                                <label>{t("ulazne.km")}:</label>
                                <InputNumber value={stavka.km} useGrouping={false}
                                    onValueChange={(e) => izmeniStavku("km", e.value)} />
                            </div>
                            <div className="col-12 md:col-2">
                                <label>{t("ulazne.datumIzvrsenja")}:</label>
                                <Calendar value={stavka.datumIzvrsenja} showIcon dateFormat="dd.mm.yy"
                                    mask="99.99.9999"
                                    onChange={(e) => izmeniStavku("datumIzvrsenja", e.value)} />
                            </div>
                            <div className="col-12 md:col-6">
                                <label>{t("ulazne.radniNalog")}:</label>
                                <CustomDropdown name="id_radni_nalog" optionLabel="label" showClear
                                    value={stavka.id_radni_nalog} options={naloziZaStavku}
                                    placeholder={t("ulazne.phRadniNalog")}
                                    emptyMessage={t("ulazne.nemaNalogaUPeriodu")}
                                    onChange={(e) => izmeniStavku("id_radni_nalog", e.target.value ?? null)} />
                            </div>
                            <div className="col-12 md:col-2 ulazne-kvacica" style={{ alignSelf: "end" }}>
                                <Checkbox inputId="trosakNaTuri" checked={!!stavka.trosakNaTuri}
                                    onChange={(e) => izmeniStavku("trosakNaTuri", e.checked)} />
                                <label htmlFor="trosakNaTuri">{t("ulazne.trosakNaTuri")}</label>
                            </div>
                        </>
                    )}
                </div>

                <div className="formUnosZap buttonUnosZap">
                    <button type="button" className="btn btn-success" onClick={sacuvajStavku}>
                        {t("common.save")}
                    </button>
                </div>
            </Dialog>

            {/* ─────────── stavka → radni nalog ─────────── */}
            <Dialog header={t("ulazne.poveziStavkuSaNalogom")} visible={vezaDialog}
                style={{ width: "40rem" }} breakpoints={{ "768px": "95vw" }}
                onHide={() => setVezaDialog(false)}>
                <div className="gridFakture ulazne-grid">
                    <div className="polje-puno col-12">
                        <label>{t("ulazne.stavka")}: <span className="iz-obavezno">*</span></label>
                        <CustomDropdown name="vezaStavka" optionLabel="label" value={veza.id_stavka}
                            options={stavkeZaVezu} placeholder={t("ulazne.phStavkaZaVezu")} style={{ width: "100%" }}
                            onChange={(e) => setVeza({ id_stavka: e.target.value, id_radni_nalog: null })} />
                    </div>
                    <div className="polje-puno col-12">
                        <label>{t("ulazne.radniNalog")}: <span className="iz-obavezno">*</span></label>
                        <CustomDropdown name="vezaNalog" optionLabel="label" value={veza.id_radni_nalog}
                            options={naloziZaVozilo(voziloIzabraneStavke)} placeholder={t("ulazne.phRadniNalog")}
                            emptyMessage={t("ulazne.nemaNalogaUPeriodu")} style={{ width: "100%" }}
                            disabled={!veza.id_stavka}
                            onChange={(e) => setVeza((p) => ({ ...p, id_radni_nalog: e.target.value }))} />
                        <small className="ulazne-veza-napomena">
                            {voziloIzabraneStavke ? t("ulazne.naloziZaVozilo") : t("ulazne.naloziUPeriodu")}
                        </small>
                    </div>
                </div>
                <div className="formUnosZap buttonUnosZap">
                    <button type="button" className="btn btn-success"
                        disabled={!veza.id_stavka || !veza.id_radni_nalog}
                        onClick={() => veziNalog(veza.id_stavka, veza.id_radni_nalog)}>
                        {t("common.save")}
                    </button>
                </div>
            </Dialog>

            {/* ─────────── nov artikal — isti unos kao u Magacinu → Nova prijemnica ─────────── */}
            <Dialog header={t("ulazne.noviArtikal")} visible={artikalModal}
                style={{ width: "80vw" }} breakpoints={{ "768px": "95vw" }}
                onHide={() => { setArtikalModal(false); ucitajSifarnike(); }}>
                {artikalModal && (
                    <UnosArtiklaKonfig setChangeArtical={(v) => { if (!v) { setArtikalModal(false); ucitajSifarnike(); } }} />
                )}
            </Dialog>

            {/* ─────────── mali modal: dopuna šifarnika ─────────── */}
            <Dialog visible={!!sifarnikModal} style={{ width: "28rem" }} onHide={() => setSifarnikModal(null)}
                header={`${sifarnikPodesavanja.id ? t("ulazne.izmeniUSifarniku") : t("ulazne.dodajUSifarnik")}: ${
                    sifarnikModal === "VRSTA" ? t("troskovi.vrsta") : sifarnikModal === "PODVRSTA" ? t("troskovi.podvrsta") : t("ulazne.kategorijaTroska")}`}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {sifarnikModal === "PODVRSTA" && (
                        <>
                            <label>{t("troskovi.vrsta")}:</label>
                            <CustomDropdown name="spVrsta" optionLabel="label" value={sifarnikPodesavanja.id_vrsta_troska}
                                options={sifarnici.VrsteTroskaDB || []} style={{ width: "100%" }}
                                onChange={(e) => setSifarnikPodesavanja((p) => ({ ...p, id_vrsta_troska: e.target.value }))} />
                        </>
                    )}
                    <label>{t("ulazne.naziv")}: <span className="iz-obavezno">*</span></label>
                    <InputText value={sifarnikNaziv} autoFocus
                        onChange={(e) => setSifarnikNaziv(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") sacuvajSifarnik(); }} />
                    {sifarnikModal === "VRSTA" && (
                        <>
                            <label>{t("troskovi.trosakTure")}:</label>
                            <CustomDropdown name="spTura" optionLabel="label" showClear style={{ width: "100%" }}
                                value={sifarnikPodesavanja.id_vrsta_troska_ture} options={sifarnici.TroskoviTureDB || []}
                                placeholder={t("troskovi.phTrosakTure")}
                                onChange={(e) => setSifarnikPodesavanja((p) => ({ ...p, id_vrsta_troska_ture: e.target.value ?? null }))} />
                        </>
                    )}
                    {sifarnikModal === "PODVRSTA" && (
                        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flex: 1 }}>
                                <label>{t("troskovi.stopaPDV")}:</label>
                                <InputNumber value={sifarnikPodesavanja.stopaPDV} mode="decimal" minFractionDigits={0}
                                    maxFractionDigits={2} suffix=" %" placeholder={t("troskovi.phBezPredloga")}
                                    onValueChange={(e) => setSifarnikPodesavanja((p) => ({ ...p, stopaPDV: e.value }))} />
                            </div>
                            <div className="ulazne-kvacica" style={{ display: "flex", gap: "0.4rem", alignItems: "center", paddingBottom: "0.5rem" }}>
                                <Checkbox inputId="spPovrat" checked={!!sifarnikPodesavanja.povratPDV}
                                    onChange={(e) => setSifarnikPodesavanja((p) => ({ ...p, povratPDV: e.checked }))} />
                                <label htmlFor="spPovrat">{t("troskovi.povratPDV")}</label>
                            </div>
                        </div>
                    )}
                    <div className="formUnosZap buttonUnosZap">
                        <button type="button" className="btn btn-success" onClick={sacuvajSifarnik}>
                            {t("common.save")}
                        </button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default UlaznaFakturaForma;
